/**
 * THE SHOP'S DAY, NOT THE DEVICE'S — AND NOT SUPERCANIC'S EITHER.
 *
 * Anthony, on the schedule: "it seems to not be sorting the day on PST."
 *
 * Every date on that page was read in whatever zone the browser happened
 * to be set to. On a laptop in California that is right by accident. On
 * the shop iPad, on a phone that has not caught up after a drive, or on
 * anything set to UTC, every job booked after 5pm lands on TOMORROW,
 * because 5pm Pacific is already the next day in UTC. A board that moves
 * the evening's work to the wrong day is worse than one showing nothing,
 * because it looks right.
 *
 * ─── WHY THIS FILE CHANGED, 2026-09-18 ─────────────────────────────────
 *
 * It used to fix that by pinning the day to America/Los_Angeles, under a
 * header that said "Supercanic is one shop in one zone and always has
 * been." That stopped being true. Wrench Services is Denver, R&T's Lock
 * and Key is Chicago, NYIA is New York. Pinned to Pacific, their boards
 * rolled over at one, two and three in the morning local: an evening job
 * on the schedule under tomorrow's heading, and a "what you got through
 * today" tally that kept counting last night's work into this morning.
 *
 * `server/desk/zone.ts` and `mayText()` were fixed the same day and the
 * SCREENS were not, which is the half of a coupled change that a customer
 * never meets and the shop looks at all day.
 *
 * ─── THE ZONE IS AN ARGUMENT, BECAUSE THE SERVER IMPORTS THIS ──────────
 *
 * Every function here takes the zone. None of them remember one.
 *
 * That is not ceremony. server/tasks/quote-chase.ts imports this file and
 * runs inside inShop() for a different account every few seconds, so a
 * module-level "current zone" would be read by whichever shop asked last
 * — the exact bug server/desk/zone.ts exists to prevent, moved into the
 * shared layer where it would be harder to see.
 *
 * The SCREENS do have one shop at a time, and threading a zone through
 * every render would mean touching every drawing function. So the primed
 * holder lives in src/ui/shopclock.ts, next to src/ui/words.ts, which
 * solved this same problem for "vehicle or door" and whose header explains
 * why it has to stay synchronous. Pure arithmetic here, one primed answer
 * there, exactly as trade.ts and words.ts already split.
 *
 * ─── AND NO OFFSET MATHS OF ITS OWN ────────────────────────────────────
 *
 * There was a second copy of the two-pass offset calculation in this file.
 * It is gone: `startOfWallDay` and `ymdIn` in src/domain/zoneclock.ts are
 * the only copy now, and this module is the calendar-grid layer over them.
 * If you find yourself reaching for Intl with a timeZone below this line,
 * it belongs in zoneclock.ts instead.
 *
 * ─── THE TWO KINDS OF DATE, KEPT APART ─────────────────────────────────
 *
 * An INSTANT is a moment, which is what `scheduled_at` holds. A CALENDAR
 * DAY is a square on a grid. Both are `Date` here, which is JavaScript's
 * fault rather than a choice, so the rule is stated once:
 *
 *   a calendar day is a Date whose LOCAL fields are the shop's date,
 *
 * which lets ordinary grid arithmetic — getDate() + 1, setMonth, getDay —
 * keep working untouched. Only two functions cross between the two kinds,
 * and both are named for it: `dayIn` takes an instant to the calendar day
 * it falls on in the shop's zone, and `startOfDayIn` takes a calendar day
 * back to the moment that shop's midnight actually begins, which is the
 * only form the database can be asked about.
 */

import { dayStartIn, startOfWallDay, ymdIn } from "./zoneclock.ts";

export { PACIFIC, usableZone } from "./zoneclock.ts";

/**
 * "2026-09-03" from a calendar day. Local fields, because that is what one
 * is — so this takes no zone, and must never be handed an instant.
 */
export const key = (cal: Date): string =>
  `${cal.getFullYear()}-${String(cal.getMonth() + 1).padStart(2, "0")}`
  + `-${String(cal.getDate()).padStart(2, "0")}`;

/** The calendar day an instant falls on, in `zone`. */
export function dayIn(zone: string, at: Date): Date {
  const [y, m, d] = ymdIn(zone, at).split("-").map(Number);
  return new Date(y!, m! - 1, d!);
}

/**
 * The key of the day an INSTANT falls on, in `zone`. This is the one for
 * job times.
 *
 * Read straight off the formatter rather than through dayIn(), so a shop
 * date that JavaScript's local-field Date cannot hold — the hour that does
 * not exist on a spring-forward morning — still keys correctly.
 */
export const dayKeyIn = (zone: string, at: Date): string => ymdIn(zone, at);

/** The moment `zone`'s midnight begins on that calendar day. */
export const startOfDayIn = (zone: string, cal: Date): Date =>
  startOfWallDay(zone, key(cal));

/**
 * The instant the shop's day started, as an ISO string.
 *
 * This is what a daily tally counts against, so it has to be the shop's
 * midnight and not the browser's. desk.ts had its own hand-rolled version.
 * Two answers to "when did today start" is one more than a shop can have.
 */
export const dayStartIsoIn = (zone: string, at = new Date()): string =>
  dayStartIn(zone, at).toISOString();

/** The time of day, in `zone`. "9:30", or "no time". */
export const timeIn = (zone: string, iso: string | null): string => iso
  ? new Date(iso).toLocaleTimeString("en-US",
      { timeZone: zone, hour: "numeric", minute: "2-digit" }).replace(":00", "")
  : "no time";
